import Button from "@/common/elements/Button";
import Table from "@/common/elements/Table";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
} from "@/common/styles";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import MedicalQuestionnaireModal from "@/components/InmateDetails/MedicalQuestionnaireModal";
import {
  checkGlobalClientParams,
  getRefListNameByID,
  hasCheckSecurity,
  isNonEmptyVariable,
} from "@/util/common_function";
import { Form, Popconfirm } from "antd";
import dayjs, { isDayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidV4 } from "uuid";
import API_ENDPOINTS from "@/util/api";
import { evaluateAndApplyQuestionnaireFlags } from "@/util/questionnaireFlagEngine";
import { getQuestionnaireInvolvements } from "@/components/BookingsDetails/JacketDetails/flagUtils";
import { evaluateAndApplyQueueStepEffects } from "@/util/questionnaireEngine";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import Modal from "@/common/elements/Modal";
import api from "@/services/axios_custom";
import {
  MODULES_LIST,
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const MEDICAL_QUESTIONNAIRES_HEADERS = [
  { key: "id", text: "ID" },
  { key: "dateAdded", text: "Date Added" },
  { key: "timeAdded", text: "Time Added" },
  { key: "name", text: "Name" },
  { key: "totalPoints", text: "Total Points" },
  { key: "creatorStaff", text: "Officer Created" },
];

const MedicalQuestionnaireList = ({}) => {
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings,
  );
  const dispatch = useDispatch();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [medicalQuestionnaires, setMedicalQuestionnaires] = useState([]);
  const [singleMedicalQuestionnaires, setSingleMedicalQuestionnaires] =
    useState(null);
  const [isModalShown, setIsModalShown] = useState(false);
  const [isEdit, setisEdit] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [questionnaireShow, setQuestionnaireShow] = useState(false);
  // undefined (not "") so the Select shows its placeholder when nothing is picked.
  const [selectQustionnaire, setSelectQustionnaire] = useState(undefined);
  const [questionnaireList, setQuestionnaireList] = useState([]);
  const [isApiLoading, setIsApiLoading] = useState(false);
  const fullSearch = useSelector((state) => state.fullSearch);

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md).
  const {
    headers: medicalQuestionnairesHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig(
    "Bookings:MedicalQuestionnaires",
    MEDICAL_QUESTIONNAIRES_HEADERS,
  );

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userDetails"));
    if (isNonEmptyVariable(user)) {
      setUserDetails(user);
    }

    getQuestionnaireListFromAPI();
  }, []);

  useEffect(() => {
    if (bookings !== null) {
      let medicalQuestionnairesCopy = bookings?.medicalQuestionnaires?.filter(
        (m) => {
          return m?.questionnaireInstances?.length !== 0;
        },
      );

      if (isNonEmptyVariable(medicalQuestionnairesCopy)) {
        medicalQuestionnairesCopy = medicalQuestionnairesCopy?.map((mqc) => {
          const questionnaire = questionnaireList?.find(
            (ql) =>
              ql?.questionnaireID ===
              mqc?.questionnaireInstances?.[0]?.questionnaireId,
          );
          if (isNonEmptyVariable(questionnaire)) {
            return {
              ...mqc,
              uuid: uuidV4(),
              totalPoints: mqc?.questionnaireInstances?.[0]?.totalPoints,
              name: questionnaire?.questionnaireName,
            };
          }
        });
        setMedicalQuestionnaires(() => medicalQuestionnairesCopy);
      } else {
        setMedicalQuestionnaires(() => []);
      }
    } else {
      setMedicalQuestionnaires(() => []);
    }
  }, [bookings, questionnaireList]);

  const asyncFilter = async (array, asyncPredicate) => {
    const results = await Promise.all(array.map(asyncPredicate));
    return array.filter((_, index) => results[index]);
  };

  const getQuestionnaireListFromAPI = async () => {
    try {
      setIsApiLoading(true);
      const url = API_ENDPOINTS.config.questionnaire.questionnairesList;
      const { data } = await api.get(url);
      if (data?.length) {
        let filtered = await asyncFilter(data, async (d) => {
          await new Promise((res) => setTimeout(res, 0));
          return checkGlobalClientParams(
            MODULES_LIST?.BOOKINGS,
            "",
            d?.questionnaireName,
          ); // keep even numbers
        });

        if (isNonEmptyVariable(filtered)) {
          filtered = filtered?.map((f) => {
            return {
              ...f,
              questionGroups: f?.questionGroups?.map((qg) => {
                if (qg?.enabled) {
                  return {
                    ...qg,
                    questions: qg?.questions?.filter((q) => q?.enabled),
                  };
                }
              }),
            };
          });

          filtered = filtered?.map((f) => {
            return {
              ...f,
              questionGroups: f?.questionGroups?.filter((f1) =>
                isNonEmptyVariable(f1),
              ),
            };
          });
        }

        setQuestionnaireList(() => filtered);
      }
    } catch (error) {
      setQuestionnaireList(() => []);
    } finally {
      setIsApiLoading(false);
    }
  };

  const onModalShow = () => {
    setIsModalShown(() => true);
  };

  const onAddQuestionnaireClick = () => {
    cancelAction();
    onModalShow();
  };

  const onEditQuestionnaireClick = (val = null) => {
    if (val?.questionnaireInstances?.[0]?.questionnaireId) {
      const questionnaire = questionnaireList?.find(
        (m) =>
          m?.questionnaireID ===
          val?.questionnaireInstances?.[0]?.questionnaireId,
      );

      const newVal = {
        ...val,
        questionnaireInstances: val?.questionnaireInstances?.map((item) => {
          return {
            ...item,
            questionAnswers: item?.questionAnswers?.map((qa) => {
              if (
                isNonEmptyVariable(qa?.answer) &&
                isValidDateRegex(qa?.answer)
              ) {
                return {
                  ...qa,
                  answer: dayjs(qa?.answer),
                };
              } else {
                return qa;
              }
            }),
          };
        }),
      };
      setQuestionnaire(questionnaire);
      setSingleMedicalQuestionnaires(newVal);
      setisEdit(true);
      onModalShow();
    } else if (isNonEmptyVariable(questionnaire)) {
      setisEdit(true);
      onModalShow();
    }
  };

  const getAnswer = async (result) => {
    let medical_questionnaires = medicalQuestionnaires;

    if (isNonEmptyVariable(result)) {
      result = {
        ...result,
        questionAnswers: result?.questionAnswers?.map((q) => {
          if (isDayjs(q.answer)) {
            q.answer = dayjs(q.answer)?.format("MM/DD/YYYY");
          }
          return q;
        }),
      };
    }
    // Config-driven flag handling - see src/util/questionnaireFlagEngine.js.
    // Stored on this medical questionnaire's own cdInvolvement (singular -
    // matches the API's nested MedicalQuestionnaires.cdInvolvement shape, see
    // bookings.modal.js), not the booking-root cdInvolvements array, same as
    // Classification (see ClassificationQuestionnairesList.jsx). The header
    // flag badges / Flags dialog read both locations back together - see
    // getAllInvolvements in flagUtils.js.
    const { cdInvolvements: cdInvolvement, removedInvIds } =
      setJacketFlagsForQuestionnaire(result);
    removedInvIds.forEach((invId) =>
      dispatch(setDeleteAPis(API_ENDPOINTS.jacketFlags + invId)),
    );

    const questionnaires = {
      bookingID: bookings?.bookingId,
      creatorStaffId: !fullSearch ? userDetails?.staffId : null,
      medicalQuestionnaireId:
        singleMedicalQuestionnaires?.medicalQuestionnaireId || null,
      cdInvolvement,
      questionnaireInstances: [result],
    };

    console.log("singleMedicalQuestionnaires", singleMedicalQuestionnaires);

    // Config-driven Booking Queue Step handling - Set/Clear Queue Step side
    // effects only, evaluated at Save (see applyQueueStepEffectsForQuestionnaire
    // below).
    const { bookingQueueDetails } = applyQueueStepEffectsForQuestionnaire(result);

    if (isEdit) {
      medical_questionnaires = medical_questionnaires?.map((cq) => {
        if (cq?.uuid === singleMedicalQuestionnaires?.uuid) {
          return {
            ...questionnaires,
            questionnaireInstances: questionnaires?.questionnaireInstances?.map(
              (qi) => {
                return {
                  ...qi,
                  questionnaireInstanceId:
                    singleMedicalQuestionnaires?.questionnaireInstances?.[0]
                      ?.questionnaireInstanceId,
                  questionAnswers: qi?.questionAnswers?.map((qa) => {
                    const question =
                      singleMedicalQuestionnaires?.questionnaireInstances?.[0]?.questionAnswers?.find(
                        (q) => q?.questionId === qa?.questionId,
                      );

                    return {
                      ...question,
                      ...qa,
                      answerDate: !fullSearch
                        ? dayjs()?.format("MM/DD/YYYY")
                        : null,
                    };
                  }),
                };
              },
            ),
          };
        }
        return cq;
      });
    } else {
      medical_questionnaires.push(questionnaires);
    }

    console.log("medical_questionnaires", medical_questionnaires);

    dispatch(
      setBookings({
        medicalQuestionnaires: medical_questionnaires,
        bookingsQueue: [{ bookingQueueDetails }],
      }),
    );

    cancelAction();
  };

  // Flag handling for this questionnaire - config-driven and generic (see
  // src/util/questionnaireFlagEngine.js for the full rules). Covers both:
  //   - per-question "Set/Clear Jacket Flag" side effects (e.g. a single
  //     Yes/No question wired straight to a flag, independent of scoring), and
  //   - questionnaire/group-level jacketFlagTrigger + scoreThreshold (set once
  //     points reach the threshold, cleared again if they later drop below it).
  // Idempotent: re-saving the same answers never creates a duplicate flag, and
  // an answer change that no longer triggers a flag retracts it.
  function setJacketFlagsForQuestionnaire(result) {
    // Link the flag back to the medical questionnaire that triggered it so
    // "Related Record Summary" can resolve it later. On a new, unsaved
    // questionnaire the ID isn't assigned yet (relatedObjectID stays null
    // until Save); flagUtils.js's getRelatedRecordSummary/findQuestionnaire
    // falls back to the one still-ID-less draft in medicalQuestionnaires to
    // resolve it in the meantime - same as Classification (see
    // ClassificationQuestionnairesList.jsx) - so relatedObjectType is always
    // "MedicalQuestionnaires", never a "Bookings" fallback.
    const relatedQuestionnaireId =
      singleMedicalQuestionnaires?.medicalQuestionnaireId;

    return evaluateAndApplyQuestionnaireFlags({
      questionnaire,
      questionAnswers: result?.questionAnswers,
      questionGroupPoints: result?.questionGroupPoints,
      totalPoints: result?.totalPoints,
      // This medical questionnaire's own prior flags, not the booking-root
      // cdInvolvements array - see the cdInvolvement assignment in
      // getAnswer above.
      cdInvolvements: getQuestionnaireInvolvements(singleMedicalQuestionnaires),
      context: {
        invObjId: bookings?.bookingId,
        // The backend stores a saved flag's invObjId as the jacketId, so a
        // re-edit must also accept that when matching its own saved flags.
        altInvObjId: bookings?.jacketId,
        invCreatedBy: userDetails?.staffId,
        invCreatedDate: dayjs().format("MM/DD/YYYY HH:mm:ss"),
        relatedObjectType: "MedicalQuestionnaires",
        relatedObjectID: relatedQuestionnaireId ?? null,
      },
    });
  }

  // Booking Queue Step handling for this questionnaire - config-driven and
  // generic (see src/util/questionnaireEngine.js). Only Set/Clear Queue Step
  // side effects are evaluated at Save, matching legacy's fValidating-only
  // gate for this side-effect type.
  function applyQueueStepEffectsForQuestionnaire(result) {
    return evaluateAndApplyQueueStepEffects({
      questionnaire,
      questionAnswers: result?.questionAnswers,
      bookingQueueDetails: bookings?.bookingsQueue?.[0]?.bookingQueueDetails,
      context: {
        completionDate: dayjs().format("MM/DD/YYYY"),
        completionTime: dayjs().format("HH:mm"),
        completionStaffID: userDetails?.staffId,
        creatorStaffID: userDetails?.staffId,
      },
    });
  }

  function isValidDateRegex(dateString) {
    const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    return regex.test(dateString);
  }

  const onRowClick = (val) => {
    const questionnaire = questionnaireList?.find(
      (m) =>
        m?.questionnaireID ===
        val?.questionnaireInstances?.[0]?.questionnaireId,
    );

    const newVal = {
      ...val,
      questionnaireInstances: val?.questionnaireInstances?.map((item) => {
        return {
          ...item,
          questionAnswers: item?.questionAnswers?.map((qa) => {
            if (
              isNonEmptyVariable(qa?.answer) &&
              isValidDateRegex(qa?.answer)
            ) {
              return {
                ...qa,
                answer: dayjs(qa?.answer),
              };
            } else {
              return qa;
            }
          }),
        };
      }),
    };

    setQuestionnaire(questionnaire);
    setSingleMedicalQuestionnaires(newVal);
  };

  const cancelAction = () => {
    setSingleMedicalQuestionnaires(null);
    setisEdit(false);
    setIsModalShown(false);
  };

  const deleteQuestionnaire = () => {
    const icq = medicalQuestionnaires?.filter(
      (icq) => icq?.uuid !== singleMedicalQuestionnaires?.uuid,
    );
    dispatch(
      setBookings({
        medicalQuestionnaires: icq,
      }),
    );

    if (
      isNonEmptyVariable(singleMedicalQuestionnaires?.medicalQuestionnaireId)
    ) {
      const url =
        API_ENDPOINTS.medicalQuestionnaires +
        singleMedicalQuestionnaires?.medicalQuestionnaireId;
      dispatch(setDeleteAPis(url));
    }

    cancelAction();
  };

  const onQuestionnairesList = (v) => {
    setSelectQustionnaire(v);
    const questionnaire = questionnaireList?.find(
      (m) => m?.questionnaireName === v,
    );

    setQuestionnaire(questionnaire);
  };

  const handleOpen = () => {
    setSelectQustionnaire(undefined);
    setQuestionnaireShow(() => true);
    setQuestionnaire(null);
  };

  const handleCancel = () => {
    setQuestionnaireShow(() => false);
  };

  const handleSubmit = () => {
    handleCancel();
    onAddQuestionnaireClick();
  };

  return (
    <>
      <StyledGridRow justify="end">
        <StyledGridCol style={{ paddingBottom: "10px" }}>
          <StyledFlex align="center" gap="10px">
            <Button
              onClick={handleOpen}
              disabled={
                !bookingsFieldsEnabled ||
                isConfiguring ||
                !hasCheckSecurity(
                  Security_Types.functionality,
                  Entity_types.subModule,
                  LABELS_CAPITAL.ADD,
                  MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                  SUB_MODULES_LIST_WITH_ID.Inmate_Medical,
                )
              }
              icon={<PlusOutlined />}
            >
              Add
            </Button>
            <Button
              onClick={onEditQuestionnaireClick}
              disabled={singleMedicalQuestionnaires === null || isConfiguring}
              icon={<EditOutlined />}
            >
              Details
            </Button>
            <Popconfirm
              title="Delete the medical questionnaires"
              description="Are you sure to delete this medical questionnaires?"
              onConfirm={deleteQuestionnaire}
              onCancel={() => {}}
              okText="Yes"
              cancelText="No"
            >
              <Button
                color="primary"
                type="primary"
                icon={<DeleteOutlined />}
                disabled={
                  singleMedicalQuestionnaires === null ||
                  !bookingsFieldsEnabled ||
                  isConfiguring ||
                  !hasCheckSecurity(
                    Security_Types.functionality,
                    Entity_types.subModule,
                    LABELS_CAPITAL.DELETE,
                    MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                    SUB_MODULES_LIST_WITH_ID.Inmate_Medical,
                  )
                }
              >
                Delete
              </Button>
            </Popconfirm>

            <Button
              onClick={cancelAction}
              disabled={singleMedicalQuestionnaires === null || isConfiguring}
              icon={<StopOutlined />}
            >
              Cancel
            </Button>

            {/* Config — drag-reorder/resize columns */}
            {!isConfiguring ? (
              <Button htmlType="button" onClick={startConfiguring}>
                {configButtonLabel}
              </Button>
            ) : (
              <Popconfirm
                title="Do you want to save the current list view configuration?"
                onConfirm={confirmSave}
                onCancel={discardChanges}
                okText="Yes"
                cancelText="No"
              >
                <Button htmlType="button">{configButtonLabel}</Button>
              </Popconfirm>
            )}
          </StyledFlex>
        </StyledGridCol>
      </StyledGridRow>

      {hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.VIEW,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Inmate_Medical,
      ) && (
        <Table
          ref={dataTableRef}
          headers={medicalQuestionnairesHeaders}
          draggable={isConfiguring}
          data={medicalQuestionnaires?.map((m, i) => {
            return {
              ...m,
              id: i + 1,
              creatorStaff: getRefListNameByID("", m?.creatorStaffId, true),
            };
          })}
          onRowClick={onRowClick}
          onRowDoubleClick={(value) => {
            // onRowClick(value);
            onEditQuestionnaireClick(value);
          }}
          selected={singleMedicalQuestionnaires === null}
          isAPiCallLoading={isApiLoading}
        />
      )}

      {isModalShown && (
        <MedicalQuestionnaireModal
          show={isModalShown}
          onHide={cancelAction}
          questionnaire={questionnaire}
          getAnswer={getAnswer}
          singleQuestionnaire={singleMedicalQuestionnaires}
          isEdit={isEdit}
        />
      )}

      <Modal
        header="Choose a Questionnaire"
        show={questionnaireShow}
        designPattern="ant"
        handleCancel={handleCancel}
        handleSubmit={handleSubmit}
        onHide={handleCancel}
        footerSubmitButtonText="OK"
      >
        <FormItem label="Questionnaires list">
          <Select
            placeholder="Choose a questionnaires"
            options={questionnaireList?.map((m) => {
              return {
                key: m?.questionnaireName,
                value: m?.questionnaireName,
                label: m?.questionnaireName,
              };
            })}
            value={selectQustionnaire}
            onChange={onQuestionnairesList}
          ></Select>
        </FormItem>
      </Modal>
    </>
  );
};
export default MedicalQuestionnaireList;
